import { useQuery } from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import pipelineKeys from '@/api/queries/pipelines/keyFactory';
import { graphql } from '@/gql';
import { GetPipelineQuery } from '@/gql/graphql';

const GET_PIPELINE = graphql(`
  query GetPipeline($id: ID!) {
    pipeline(id: $id) {
      id
      publicId
      title
      colour
      isDeletable {
        result
        reasons
      }
      stages {
        id
        publicId
        title
        index
        colour
        isDeletable {
          result
          reasons
        }
        guard {
          # Named fragment not used here to avoid exceeding server's query depth limit.
          # Named fragment spreads (e.g. ...PipelineGuard) add +1 depth before expanding,
          # while inline fragments (... on Type) don't add depth since they're type narrowing.
          ... on IPipelineGuard {
            validityResult {
              isValid
              reasons
            }
          }
          ... on PipelineGuardOr {
            __typename
            children {
              ... on PipelineGuardAnd {
                __typename
                children {
                  ... on IPipelineGuard {
                    validityResult {
                      isValid
                      reasons
                    }
                  }
                  ... on PipelineGuardSimpleCondition {
                    __typename
                    type
                  }
                  ... on PipelineGuardProjectHasTagCondition {
                    __typename
                    tag {
                      publicId
                      name
                      colour
                    }
                  }
                  ... on PipelineGuardCustomFieldCondition {
                    ...CustomFieldGuardCondition
                  }
                  ... on PipelineGuardNot {
                    __typename
                    child {
                      ... on IPipelineGuard {
                        validityResult {
                          isValid
                          reasons
                        }
                      }
                      ... on PipelineGuardSimpleCondition {
                        __typename
                        type
                      }
                      ... on PipelineGuardProjectHasTagCondition {
                        __typename
                        tag {
                          publicId
                          name
                          colour
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`);

export type PipelineStage = NonNullable<
  GetPipelineQuery['pipeline']
>['stages'][0];

const useGetPipeline = (publicId?: string) => {
  const { data, ...rest } = useQuery({
    enabled: !!publicId,
    queryKey: pipelineKeys.pipeline(publicId!),
    queryFn: () => {
      return gqlClient.request(GET_PIPELINE, {
        id: publicId!,
      });
    },
  });

  return {
    data,
    pipeline: data?.pipeline,
    ...rest,
  };
};

export default useGetPipeline;
